import { useEffect, useImperativeHandle, useState, type Ref } from "react";
import { useTranslation } from "react-i18next";

import { FOCUS_RING_INSET } from "@lytra/frontend-common/components/v2/lib/focus";
import { cn } from "@/lib/utils";
import {
  ColleagueOptionContent,
  PICKER_ROW,
  PickerMessageRow,
  PickerSkeletonRows,
} from "./colleaguePickerRows";
import { sanitizeMentionLabel } from "./noteMentions";

/** The part of an agent the picker reads (the `agents` query: the tenant's colleagues, removed users excluded). */
export interface PickerAgent {
  id: string;
  givenName?: string | null;
  familyName?: string | null;
  emailAddress?: string | null;
}

/** One selectable colleague. `label` is what the chip and the stored token carry. */
export interface Colleague {
  id: string;
  label: string;
  email: string | null;
  /** Another colleague has the same full name: the list tells them apart by email. */
  showEmail: boolean;
  givenName: string;
  familyName: string;
}

/** Lower-case, no diacritics, ß as ss: "jurgen" finds "Jürgen", "weiss" finds "Weiß". */
function normalize(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
    .toLowerCase()
    .trim();
}

/**
 * The colleagues the picker offers for what was typed after the `@` (D-14). The viewer is
 * left out (nobody needs a request from their own note). A colleague matches when the query
 * starts their given name, their family name or "given family"; the list is ordered by family
 * name then given name in German collation. Two colleagues with the same full name are flagged
 * so the list can show their email. Pure.
 */
// The filter is the panel's contract (plan 09 and the tests import both from here); a pure function, no fast-refresh state at stake.
// eslint-disable-next-line react-refresh/only-export-components
export function filterColleagues(
  agents: readonly PickerAgent[],
  query: string,
  viewerId: string | undefined,
): Colleague[] {
  const colleagues = agents
    .filter((agent) => agent.id !== viewerId)
    .map((agent): Colleague => {
      const givenName = (agent.givenName ?? "").trim();
      const familyName = (agent.familyName ?? "").trim();
      const full = `${givenName} ${familyName}`.trim();
      return {
        id: agent.id,
        // An agent with no name at all still has to be pickable and the token needs a label.
        label: sanitizeMentionLabel(full || (agent.emailAddress ?? "")),
        email: agent.emailAddress ?? null,
        showEmail: false,
        givenName,
        familyName,
      };
    });

  const counts = new Map<string, number>();
  for (const colleague of colleagues) {
    const key = normalize(colleague.label);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const needle = normalize(query);
  return colleagues
    .filter((colleague) => {
      if (needle === "") return true;
      return [
        colleague.givenName,
        colleague.familyName,
        `${colleague.givenName} ${colleague.familyName}`,
      ].some((candidate) => normalize(candidate).startsWith(needle));
    })
    .map((colleague) => ({
      ...colleague,
      showEmail:
        (counts.get(normalize(colleague.label)) ?? 0) > 1 &&
        colleague.email !== null,
    }))
    .sort(
      (a, b) =>
        a.familyName.localeCompare(b.familyName, "de") ||
        a.givenName.localeCompare(b.givenName, "de"),
    );
}

export type MentionListState = "ready" | "loading" | "error" | "cap";

/** What the suggestion renderer calls for keys the editor receives while the picker is open. */
export interface MentionListHandle {
  /** True when the key was used; false hands it back to the editor (Escape, or nothing to select). */
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

export interface MentionListProps {
  /** Stable id of the listbox: the editor's `aria-controls` points at it. */
  id: string;
  items: Colleague[];
  state: MentionListState;
  onSelect: (colleague: Colleague) => void;
  /** The id of the highlighted option, or null: the editor's `aria-activedescendant`. */
  onActiveChange?: (optionId: string | null) => void;
  ref?: Ref<MentionListHandle>;
}

const optionId = (listId: string, index: number) => `${listId}-option-${index}`;

/**
 * The @ picker's panel (UI-SPEC section 4). A listbox the editor drives: focus stays in the
 * editor, which carries `aria-activedescendant`; the panel never takes focus. A colleague's
 * name is rendered as a React text node, never as markup (T-02-28). States: colleagues,
 * loading (three skeleton rows), error, no match and the 20-mention cap are each one row.
 */
export function MentionList({
  id,
  items,
  state,
  onSelect,
  onActiveChange,
  ref,
}: MentionListProps) {
  const { t } = useTranslation();
  // The highlight belongs to one list of items: a new list starts at the first row.
  const [active, setActive] = useState({ items, index: 0 });
  const index = active.items === items ? active.index : 0;
  const selectable = state === "ready" && items.length > 0;
  const activeId = selectable ? optionId(id, index) : null;

  const move = (to: number) => {
    const next = (to + items.length) % items.length;
    setActive({ items, index: next });
    // The row may be below the fold of the scrolling panel.
    document
      .getElementById(optionId(id, next))
      ?.scrollIntoView?.({ block: "nearest" });
  };

  useImperativeHandle(
    ref,
    () => ({
      onKeyDown: ({ event }) => {
        if (!selectable) return false;
        switch (event.key) {
          case "ArrowDown":
            move(index + 1);
            return true;
          case "ArrowUp":
            move(index - 1);
            return true;
          case "Enter":
          case "Tab":
            onSelect(items[index]);
            return true;
          default:
            // Escape and every typed character: the editor closes or filters.
            return false;
        }
      },
    }),
    // `move` closes over the same values as the list below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectable, items, index, onSelect],
  );

  // The editor's `aria-activedescendant` follows the highlight.
  useEffect(() => {
    onActiveChange?.(activeId);
  }, [activeId, onActiveChange]);

  return (
    <div
      data-slot="mention-list"
      className="z-50 max-h-56 min-w-60 max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-popover transition-opacity duration-150 outline-hidden starting:opacity-0 motion-reduce:transition-none"
      // The click must not move focus out of the editor.
      onMouseDown={(event) => event.preventDefault()}
    >
      <div
        id={id}
        role="listbox"
        aria-label={t("tickets.notes.picker.listName")}
        aria-busy={state === "loading" || undefined}
        className="flex flex-col"
      >
        {state === "loading" && <PickerSkeletonRows />}
        {state === "error" && (
          <PickerMessageRow id={optionId(id, 0)}>
            {t("tickets.notes.picker.error")}
          </PickerMessageRow>
        )}
        {state === "cap" && (
          <PickerMessageRow id={optionId(id, 0)}>
            {t("tickets.notes.picker.cap")}
          </PickerMessageRow>
        )}
        {state === "ready" && items.length === 0 && (
          <PickerMessageRow id={optionId(id, 0)}>
            {t("tickets.notes.picker.empty")}
          </PickerMessageRow>
        )}
        {state === "ready" &&
          items.map((colleague, row) => {
            const highlighted = row === index;
            return (
              <div
                key={colleague.id}
                id={optionId(id, row)}
                role="option"
                aria-selected={highlighted}
                data-focus={highlighted ? "" : undefined}
                className={cn(
                  PICKER_ROW,
                  FOCUS_RING_INSET,
                  highlighted && "bg-hover-overlay",
                )}
                onMouseMove={() => {
                  if (!highlighted) setActive({ items, index: row });
                }}
                onClick={() => onSelect(colleague)}
              >
                <ColleagueOptionContent colleague={colleague} />
              </div>
            );
          })}
      </div>
      {state === "loading" && (
        <span role="status" className="sr-only">
          {t("tickets.notes.picker.loading")}
        </span>
      )}
    </div>
  );
}
